import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import http from '../components/http/http';
import { useTranslation } from 'react-i18next';

const TopBar = styled.div`
  width: 100%;
  height: 120px;
  background: #3baab0;
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 700px) {
    height: 70px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  padding: 0;
  background: #fff;
  color: #2a8f95;
  cursor: pointer;
  font-weight: 600;
  line-height: 1;

  @media (max-width: 700px) {
    padding: 8px 10px;
    gap: 6px;
    font-size: 14px;
  }
`;

const BackIcon = (props: React.SVGAttributes<SVGSVGElement>) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M15 18l-6-6l6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ProfileCircle = styled.div`
  width: 80px;
  height: 80px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #3baab0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;

  @media (max-width: 700px) {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }

  .profile-letter-anim {
    opacity: 1;
    transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
    will-change: opacity, transform;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .profile-letter-anim--hidden {
    opacity: 0;
    pointer-events: none;
  }
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 840px;
  margin: 0 auto;
  justify-content: center;
  min-height: 80vh;
  background: var(--global-white, #FFF);

  @media (max-width: 700px) {
    min-height: unset;
    padding: 0;
    display: block;
  }
`;
const ContentBlock = styled.div`
  display: flex;
  width: 100%;
  max-width: 840px;
  height: 700px;
  margin: 24px 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.03);
  border: 1px solid #ededed;

  @media (max-width: 700px) {
    flex-direction: column;
    margin: 0;
    max-width: 100%;
    min-height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
`;
const Sidebar = styled.div`
  width: 220px;
  background: #fff;
  border-right: 1px solid #ededed;
  padding: 32px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 12px 0 0 12px;

  @media (max-width: 700px) {
    display: none;
  }
`;
const SidebarItem = styled.div<{active?: boolean, disabled?: boolean}>`
  padding: 14px 32px;
  font-size: 15px;
  line-height: 20px;
  color: ${({active, disabled}) => disabled ? '#aaa' : active ? '#3baab0' : '#222'};
  background: ${({active}) => active ? '#e6f7f8' : 'transparent'};
  cursor: ${({disabled}) => disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  border-left: ${({active}) => active ? '4px solid #3baab0' : '4px solid transparent'};
  transition: background .2s;
  &:hover {
    background: ${({disabled}) => disabled ? 'transparent' : '#f0f0f0'};
  }

  @media (max-width: 700px) {
    padding: 10px 12px;
    font-size: 14px;
    border-left: none;
    border-bottom: ${({active}) => active ? '3px solid #3baab0' : '3px solid transparent'};
    border-radius: 0;
    min-width: 120px;
    text-align: center;
    flex: 1 0 auto;
  }
`;

const MobileFontStyle = createGlobalStyle`
  @media (max-width: 700px) {
    body, #root, [data-profile-layout] {
      font-size: 14px;
    }
    [data-profile-layout] * {
      font-size: 14px;
    }

    [data-profile-layout] h1 {
      font-size: 28px;
      font-family: Libre Baskerville;
      font-style: normal;
      font-weight: 700;
      line-height: 52px;
      letter-spacing: 0.8px;
    }
  }
`;

interface ProfileLayoutProps {
  children: React.ReactNode;
  activeTab: string;
  firstLetter?: string;
}

export default function ProfileLayout({ children, activeTab, firstLetter }: ProfileLayoutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sidebarItems = [
    { key: 'profile', label: t('profile'), path: '/profile' },
    { key: 'orders', label: t('orders'), path: '/orders' },
    { key: 'myReports', label: t('myReports'), path: '/my-reports' },
    { key: 'healthHistory', label: t('healthHistory'), path: '', disabled: true },
    // { key: 'accountSettings', label: t('accountSettings'), path: '/account-settings' },
    { key: 'logout', label: t('logout'), path: '/logout', danger: true },
  ];

  const handleLogout = async () => {
    try {
      if (typeof http !== 'undefined') {
        await http.post('/api/users/auth/logout', {});
      }
    } catch (e) {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <>
      <MobileFontStyle />
      <TopBar>
        <ProfileCircle>
          <span
            className={
              typeof firstLetter === 'string'
                ? 'profile-letter-anim'
                : 'profile-letter-anim profile-letter-anim--hidden'
            }
          >
            {typeof firstLetter === 'string' ? firstLetter : ''}
          </span>
        </ProfileCircle>
      </TopBar>
      <Wrapper data-profile-layout>
        <div style={{ width: '100%', marginTop: '30px' }}>
          <BackButton type="button" onClick={() => navigate(-1)} aria-label={t('goBack')}>
            <BackIcon />
            <span>{t('goBack')}</span>
          </BackButton>
        </div>
        <ContentBlock>
          <Sidebar>
            {sidebarItems.map((item) => (
              <SidebarItem
                key={item.key}
                active={item.key === activeTab}
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.key === 'logout') {
                    handleLogout();
                    return;
                  }
                  if (item.path) navigate(item.path, { replace: true });
                }}
                style={item.danger ? { color: '#e96863' } : {}}
              >
                {item.label}
              </SidebarItem>
            ))}
          </Sidebar>
          <div style={{ flex: 1, padding: '20px 30px', boxSizing: 'border-box' }}>
            {children}
          </div>
        </ContentBlock>
      </Wrapper>
    </>
  );
} 
